import { computed, onActivated, onMounted, ref } from "vue";
import { useTranslation } from "i18next-vue";
import BlockLibrary from "@/blocks/library";
import AssetLibrary from "@/assets/parametric-assets";
import TemplateBlocksState from "@/stores/TemplateBlocksState";
import { Facade } from "@/drawing";
import type { DwgBlockInfo } from "@/drawing/blocks";
import type { ParametricAsset } from "@/types";
import { trackTab } from "@/analytics/drawing-analytics";
import { matchesSearch, normalizeSearchText } from "@/utils/search-text";

/**
 * Catégories de l'onglet Objets, dans l'ordre d'affichage : du plus propre à
 * la mission (blocs de la légende, puis ceux déjà posés dans le dessin) au plus
 * générique (blocs Inspexio, parties d'ouvrage paramétrées).
 */
export const OBJECT_CATEGORIES = ["legend", "drawing", "dr", "assets"] as const;
export type ObjectCategory = (typeof OBJECT_CATEGORIES)[number];
export type ObjectCategoryFilter = ObjectCategory | "all";

/** Bloc prédéfini Inspexio (« DR ») : nom technique, libellé lisible, aperçu. */
export interface DrBlockEntry {
  name: string;
  label: string;
  image: string;
}

export interface ObjectsByCategory {
  legend: DwgBlockInfo[];
  drawing: DwgBlockInfo[];
  dr: DrBlockEntry[];
  assets: ParametricAsset<any>[];
}

// Catégorie choisie : gardée d'une ouverture de l'onglet à l'autre.
const selectedCategory = ref<ObjectCategoryFilter>("all");

/** L'onglet Objets rouvrira sur les parties d'ouvrage (suivi de leur ouverture). */
export const isAssetsCategorySelected = () =>
  selectedCategory.value === "assets";

const DR_BLOCKS: DrBlockEntry[] = BlockLibrary.getAllNames().map((name) => {
  const configName = BlockLibrary.getBlockConfigName(name);
  return {
    name,
    label: configName.replace(/[-_]+/g, " "),
    image: `/icons/blocks/${configName}.svg`,
  };
});

/**
 * Contenu de l'onglet Objets : les 4 catégories, filtrées par la recherche.
 */
export function useObjectsCatalog() {
  const { t } = useTranslation();
  const query = ref("");

  // Les blocs du dessin changent au fil du dessin : relus à chaque ouverture.
  const dwgBlocks = ref<DwgBlockInfo[]>([]);
  function loadDwgBlocks() {
    if (Facade.isInitialized) dwgBlocks.value = Facade.blocks.listDwgBlocks();
  }
  onMounted(loadDwgBlocks);
  onActivated(loadDwgBlocks);

  const assets = AssetLibrary.getAll();
  const assetName = (asset: ParametricAsset<any>) =>
    t(`canvas:parametricAssets.library.${asset.id}.name`);

  const isSearching = computed(() => normalizeSearchText(query.value) !== "");

  const objects = computed<ObjectsByCategory>(() => {
    const q = query.value;
    return {
      legend: TemplateBlocksState.isLoaded
        ? TemplateBlocksState.blocks.filter((b) => matchesSearch(b.name, q))
        : [],
      drawing: dwgBlocks.value.filter((b) => matchesSearch(b.name, q)),
      dr: DR_BLOCKS.filter((b) => matchesSearch(b.label, q)),
      assets: assets.filter((a) => matchesSearch(assetName(a), q)),
    };
  });

  const counts = computed(
    () =>
      Object.fromEntries(
        OBJECT_CATEGORIES.map((c) => [c, objects.value[c].length]),
      ) as Record<ObjectCategory, number>,
  );

  function selectCategory(category: ObjectCategoryFilter) {
    if (category === selectedCategory.value) return;
    selectedCategory.value = category;
    // Les parties d'ouvrage avaient leur onglet : leur ouverture reste suivie
    // dans leur famille, la page Usage du back-office ne change pas.
    if (category === "assets") trackTab("assets", "opened");
  }

  return {
    query,
    isSearching,
    selectedCategory,
    selectCategory,
    objects,
    counts,
  };
}
